<template>
  <div>
    <v-card
      rounded="lg"
      class="px-3 py-2 hoverable-card"
      outlined
    >
      <v-row
        no-gutters
        align="center"
        :style="[ editable ? { minHeight: '30px' } : undefined ]"
      >
        <v-col
          v-if="orderable"
          cols="auto"
          class="pr-3"
        >
          <v-icon
            small
            class="handle"
            style="cursor: move"
          >
            far fa-grip-lines
          </v-icon>
        </v-col>
        <v-col>
          <div
            class="font-weight-medium"
            :class="{ 'caption': dense }"
          >
            <strong>{{ item.params.title || 'Section' }}</strong>
          </div>
        </v-col>
        <v-col
          v-if="editable"
          cols="auto"
          class="show-hover"
        >
          <v-btn
            text
            color="grey darken-2"
            small
            outlined
            class="text-none mr-1"
            @click="dialog = true"
          >
            <v-icon
              x-small
              left
            >
              far fa-pen
            </v-icon>
            {{ $t('button.edit') }}
          </v-btn>
          <v-btn
            text
            color="grey darken-2"
            small
            outlined
            :disabled="item.exercises.length === 0"
            class="text-none"
            :loading="saving"
            @click="saveDialog = true"
          >
            <v-icon
              x-small
              left
            >
              far fa-save
            </v-icon>
            {{ $t('training_builder.button.save') }}
          </v-btn>
          <v-btn
            text
            color="error"
            small
            @click="$emit('delete')"
          >
            <v-icon x-small>
              far fa-trash
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>

      <div>
        <div style="position: relative">
          <div
            v-if="!item.exercises || item.exercises.length === 0"
            style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); z-index: 10"
            class="grey--text"
          >
            {{ $t('training_builder.drag_exercises.box.placeholder') }}
          </div>

          <draggable
            :list="item.exercises"
            handle=".handle"
            :group="{ name: 'exercises' }"
            class="mt-2"
            :class="{ 'grey lighten-3 rounded-lg drop-placeholder': item.exercises.length === 0}"
            style="min-height: 80px; position: relative"
            :invert-swap="true"
            :swap-treshold="0.6"
            :animation="100"
            :force-fallback="true"
            belongs-to="group"
          >
            <div
              v-for="ex in item.exercises"
              :key="ex.internal_id"
              class="mb-2"
            >
              <pause-selected-card
                v-if="ex.type === 'pause'"
                :item.sync="ex"
                :dense="dense"
                :orderable="orderable"
                :editable="editable"
                :feedback-allowed="feedbackAllowed"
                :show-media="showMedia"
                @delete="item.exercises.splice(item.exercises.findIndex(e => e.internal_id === ex.internal_id), 1)"
              />
              <superset-selected-card
                v-else-if="ex.type === 'superset'"
                :item.sync="ex"
                :dense="dense"
                :orderable="orderable"
                :editable="editable"
                :feedback-allowed="feedbackAllowed"
                :show-media="showMedia"
                :history-allowed="historyAllowed"
                :player="player"
                :players="players"
                @delete="item.exercises.splice(item.exercises.findIndex(e => e.internal_id === ex.internal_id), 1)"
                @feedback="$emit('feedback', $event)"
              />
              <exercise-selected-card-template
                v-else-if="ex.group"
                :exercise="ex"
                style="z-index: 20; position: relative"
                :dense="dense"
                :orderable="orderable"
                :editable="editable"
                :feedback-allowed="feedbackAllowed"
                :show-media="showMedia"
                :history-allowed="historyAllowed"
                :player="player"
                :players="players"
                @delete="item.exercises.splice(item.exercises.findIndex(e => e.internal_id === ex.internal_id), 1)"
                @feedback="$emit('feedback', $event)"
              />
            </div>
          </draggable>
        </div>
      </div>

      <validation-provider
        v-slot="{ errors }"
        rules="required"
      >
        <input
          type="hidden"
          :value="item.exercises.length > 0 ? true : undefined"
        >
        <div
          v-if="errors.length > 0"
          class="error--text mt-2 mb-1 text-body-1"
        >
          {{ $t("training_builder.exercise_required") }}
        </div>
      </validation-provider>
    </v-card>

    <card-dialog
      v-model="dialog"
      max-width="600px"
      :title="$t('training_builder.configure_section.dialog.header')"
    >
      <v-card-text
        class="pa-6"
      >
        <v-text-field
          v-model="params.title"
          outlined
          :label="$t('training_builder.text_input.section_title.title')"
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          color="primary"
          @click="saveParams()"
        >
          {{ $t('training_builder.button.save') }}
        </v-btn>
      </v-card-actions>
    </card-dialog>

    <card-dialog
      v-model="saveDialog"
      max-width="600px"
      :title="$t('training_builder.save_section.dialog.header')"
    >
      <v-card-text
        class="pa-6"
      >
        <p class="text-body-1">
          {{ $t("training_builder.save_section.dialog.label") }}
        </p>

        <v-combobox
          v-model="selectedTags"
          outlined
          multiple
          hide-details
          :label="$t('dropdown.section_tags.title')"
          dense
          chips
          small-chips
          deletable-chips
          :items="tags.data"
          :loading="tags.loading"
          return-object
          item-value="id"
          item-text="label"
          :delimiters="[',']"
        >
          <template v-slot:selection="data">
            <v-tooltip
              bottom
              :disabled="data.item.label && data.item.label.length < 50"
            >
              <template v-slot:activator="{ on }">
                <v-chip
                  small
                  v-on="on"
                >
                  <span style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis">
                    {{ data.item.label ? data.item.label : data.item }}
                  </span>
                  <v-icon
                    x-small
                    right
                    @click="selectedTags.splice(selectedTags.indexOf(data.item), 1)"
                  >
                    fal fa-times
                  </v-icon>
                </v-chip>
              </template>
              {{ data.item.label ? data.item.label : data.item }}
            </v-tooltip>
          </template>
        </v-combobox>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          color="primary"
          :loading="saving"
          @click="saveGroup()"
        >
          {{ $t('button.save') }}
        </v-btn>
      </v-card-actions>
    </card-dialog>
  </div>
</template>
<script>
import CardDialog from '@/components/helpers/CardDialog.vue'
import draggable from 'vuedraggable'
import PauseSelectedCard from '@/components/training-builder/PauseSelectedCard.vue'
import { TrainingExerciseApiService } from '@/data/services/TrainingExerciseApiService'
import SupersetSelectedCard from '@/components/training-builder/SupersetSelectedCard.vue'
import ExerciseSelectedCardTemplate from '@/components/training-builder/ExerciseSelectedCardTemplate.vue'
import { TagApiService } from '@/data/services/TagApiService'

export default {
  name: 'GroupSelectedCard',
  components: { ExerciseSelectedCardTemplate, SupersetSelectedCard, PauseSelectedCard, CardDialog, draggable },
  props: {
    item: {
      type: Object,
      required: true
    },
    dense: {
      type: Boolean,
      default: false
    },
    orderable: {
      type: Boolean,
      default: true
    },
    editable: {
      type: Boolean,
      default: true
    },
    feedbackAllowed: {
      type: Boolean,
      default: false
    },
    showMedia: {
      type: Boolean,
      default: false
    },
    historyAllowed: {
      type: Boolean,
      default: false
    },
    player: {
      type: Object,
      default: () => undefined
    },
    players: {
      type: Array,
      default: () => ([])
    }
  },
  data() {
    return {
      dialog: false,
      params: {
        title: ''
      },
      tags: {
        loading: false,
        data: []
      },
      selectedTags: [],
      saveDialog: false,
      saving: false
    }
  },

  computed: {
  },

  created() {
    if (!this.item.params) {
      this.item.params = { ...this.params }
    } else if (this.item.params) {
      this.params = { ...this.item.params }
    }

    if (this.item.tags) {
      this.selectedTags = [...this.item.tags]
    }

    if (this.item.configureParams) {
      this.dialog = true
      this.item.configureParams = false
    }

    this.fetchTags()
  },

  methods: {
    saveParams() {
      this.item.params = this.params

      this.dialog = false
    },
    async saveGroup() {
      this.saving = true

      try {
        const result = await TrainingExerciseApiService.saveUserGroup({
          id: this.item.userGroupId || undefined,
          params: this.item.params,
          exercises: this.item.exercises,
          tags: this.selectedTags.map(t => {
            if (Array.isArray(t)) return t.id

            return t
          })
        })

        this.saveDialog = false

        this.item.userGroupId = result.id

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.exercises_section_save.success'),
          color: 'success'
        })

        this.$emit('saved')
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.saving_selection.error'),
          color: 'error'
        })
      } finally {
        this.saving = false
      }
    },
    async fetchTags() {
      this.tags.loading = true

      try {
        const result = await TagApiService.getAll()
        this.tags.data = result.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_tags.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.tags.loading = false
      }
    }
  }
}
</script>

<style>
/*.drop-placeholder:before {
  width: 100%;
  color: #9a9a9a;
  font-size: 16px;
  content: "Drop exercise here";
  display: flex;
  align-items: center;
  justify-content: center;
}*/
</style>
